import React from "react";
import { Minus, Plus, Trash2, ShoppingBag, StickyNote } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";

const CartPanel = ({ minOrder, deliveryFee, onCheckout }) => {
  const { items, updateQty, removeItem, subtotal, itemsSubtotal, orderType, postalCode, computeUnitPrice, discountInfo } = useCart();
  const total = subtotal + (orderType === "delivery" ? deliveryFee : 0);
  // Min-order checks should consider items_subtotal BEFORE discount to avoid abuse
  const belowMin = orderType === "delivery" && itemsSubtotal < minOrder;
  const missing = Math.max(0, minOrder - itemsSubtotal);
  const lineMap = (discountInfo?.lines || []).reduce((acc, l) => { acc[l.cart_id] = l; return acc; }, {});

  return (
    <div data-testid="cart-panel" className="bg-white border border-neutral-200 rounded-xl flex flex-col">
      <div className="px-5 py-4 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Votre panier
          </h3>
          <ShoppingBag className="w-5 h-5 text-neutral-500" />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-neutral-500">
          Votre panier est vide.<br />Sélectionnez des plats pour commencer.
        </div>
      ) : (
        <>
          <div className="max-h-[380px] overflow-y-auto divide-y divide-neutral-100">
            {items.map((it) => {
              const unit = computeUnitPrice(it);
              return (
                <div
                  key={it.cart_id}
                  className="px-5 py-3 flex items-start gap-3"
                  data-testid={`cart-item-${it.cart_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold leading-snug">{it.name}</p>
                    {(it.selected_addons || []).length > 0 && (
                      <ul className="mt-1 text-[11px] text-neutral-500 space-y-0.5">
                        {it.selected_addons.map((a, i) => (
                          <li key={i}>
                            + {a.name}
                            {Number(a.price) > 0 ? ` (${Number(a.price).toFixed(2)} CHF)` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                    {it.comment && (
                      <p className="mt-1 text-[11px] text-neutral-500 flex items-start gap-1">
                        <StickyNote className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <em>{it.comment}</em>
                      </p>
                    )}
                    <p className="text-xs text-neutral-500 mt-1">{unit.toFixed(2)} CHF</p>
                    {lineMap[it.cart_id]?.discount > 0 && (
                      <p
                        data-testid={`cart-item-discount-${it.cart_id}`}
                        className="mt-1 text-[11px] font-bold text-[#2A9D8F]"
                      >
                        -{lineMap[it.cart_id].discount.toFixed(2)} CHF {lineMap[it.cart_id].label ? `· ${lineMap[it.cart_id].label}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button
                      data-testid={`cart-remove-${it.cart_id}`}
                      onClick={() => removeItem(it.cart_id)}
                      className="text-neutral-400 hover:text-[#D90429]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`cart-dec-${it.cart_id}`}
                        onClick={() => updateQty(it.cart_id, -1)}
                        className="w-7 h-7 rounded-full border border-neutral-300 flex items-center justify-center hover:bg-neutral-100"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span
                        className="text-sm font-semibold w-5 text-center"
                        data-testid={`cart-qty-${it.cart_id}`}
                      >
                        {it.quantity}
                      </span>
                      <button
                        data-testid={`cart-inc-${it.cart_id}`}
                        onClick={() => updateQty(it.cart_id, +1)}
                        className="w-7 h-7 rounded-full border border-neutral-300 flex items-center justify-center hover:bg-neutral-100"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-5 py-4 border-t border-neutral-200 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-600">Sous-total</span>
              <span className="font-semibold" data-testid="cart-items-subtotal">
                {itemsSubtotal.toFixed(2)} CHF
              </span>
            </div>
            {discountInfo?.totalDiscount > 0 && (
              <div className="flex justify-between text-[#2A9D8F]" data-testid="cart-discount-row">
                <span className="font-bold">
                  Remise
                  {discountInfo.applied?.length > 0 ? ` · ${discountInfo.applied.join(" · ")}` : ""}
                </span>
                <span className="font-bold" data-testid="cart-discount-total">
                  -{discountInfo.totalDiscount.toFixed(2)} CHF
                </span>
              </div>
            )}
            {orderType === "delivery" && (
              <div className="flex justify-between">
                <span className="text-neutral-600">Frais de livraison</span>
                <span className="font-semibold" data-testid="cart-delivery-fee">
                  {postalCode ? `${deliveryFee.toFixed(2)} CHF` : "—"}
                </span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-neutral-100">
              <span className="font-bold">Total</span>
              <span className="font-black text-lg" data-testid="cart-total">
                {total.toFixed(2)} CHF
              </span>
            </div>

            {orderType === "delivery" && postalCode && belowMin && (
              <div
                data-testid="min-order-warning"
                className="mt-2 text-xs font-medium text-[#BF0424] bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                Minimum {minOrder.toFixed(2)} CHF pour livraison. Ajoutez encore{" "}
                {missing.toFixed(2)} CHF.
              </div>
            )}

            <Button
              data-testid="checkout-button"
              onClick={onCheckout}
              disabled={belowMin || (orderType === "delivery" && !postalCode)}
              className="w-full mt-3 bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-11 font-bold tracking-wide disabled:opacity-50"
            >
              Passer commande
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CartPanel;
